import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Group from '@mui/icons-material/esm/Group';
import Engineering from '@mui/icons-material/esm/Engineering';
import Speed from '@mui/icons-material/esm/Speed';
import Payments from '@mui/icons-material/esm/Payments';
import { useSelector } from 'react-redux';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import teamService, { TeamOverviewRow } from '../../services/api/teamService';

const TEAM_TYPE_LABEL: Record<string, string> = {
  stores: 'Stores',
  construction: 'Construction',
  supervisor: 'Supervisor',
  accounts: 'Accounts',
  procurement: 'Procurement',
  sales: 'Sales',
};

const formatMoney = (value: number): string => `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface TeamsOverviewPageProps {
  embedded?: boolean;
}

export default function TeamsOverviewPage({ embedded = false }: TeamsOverviewPageProps = {}) {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<TeamOverviewRow[]>([]);
  const [scopeToProject, setScopeToProject] = useState<boolean>(true);
  const [teamTypeFilter, setTeamTypeFilter] = useState<string>('all');
  const [selected, setSelected] = useState<TeamOverviewRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await teamService.getOverview(scopeToProject ? activeProjectId ?? undefined : undefined);
        if (!cancelled) setRows(data);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load teams overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectId, scopeToProject]);

  const filtered = useMemo(
    () => (teamTypeFilter === 'all' ? rows : rows.filter((r) => r.teamType === teamTypeFilter)),
    [rows, teamTypeFilter]
  );

  const summary = useMemo(() => {
    const totalMembers = filtered.reduce((sum, r) => sum + r.memberCount, 0);
    const totalInProgress = filtered.reduce((sum, r) => sum + r.workload.inProgress, 0);
    const totalAwaitingApproval = filtered.reduce((sum, r) => sum + r.payment.awaitingApproval, 0);
    const yields = filtered.map((r) => r.performance.firstPassYieldPercent).filter((y): y is number => y !== null);
    const avgYield = yields.length > 0 ? Math.round(yields.reduce((s, y) => s + y, 0) / yields.length) : null;
    return { totalMembers, totalInProgress, totalAwaitingApproval, avgYield };
  }, [filtered]);

  const columns: Column<TeamOverviewRow>[] = [
    { id: 'name', label: 'Team', minWidth: 180 },
    {
      id: 'teamType',
      label: 'Type',
      minWidth: 110,
      format: (value: string) => TEAM_TYPE_LABEL[value] ?? value,
    },
    {
      id: 'teamLeadName',
      label: 'Lead',
      minWidth: 140,
      format: (value: string | null) => value ?? '—',
    },
    { id: 'memberCount', label: 'Members', minWidth: 80, align: 'right' },
    {
      id: 'workload',
      label: 'In Progress',
      minWidth: 100,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => `${row.workload.inProgress} / ${row.maxConcurrentTasks}`,
    },
    {
      id: 'performance',
      label: 'Tasks Done',
      minWidth: 110,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => `${row.workload.completed}`,
    },
    {
      id: 'performance',
      label: 'First-Pass Yield',
      minWidth: 130,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => {
        const y = row.performance.firstPassYieldPercent;
        if (y === null) return '—';
        return (
          <Chip size="small" label={`${y}%`} color={y >= 90 ? 'success' : y >= 75 ? 'warning' : 'error'} />
        );
      },
    },
    {
      id: 'performance',
      label: 'Open Defects',
      minWidth: 100,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => (
        row.performance.openDefects > 0 ? (
          <Typography variant="body2" fontWeight="bold" color="error.main">{row.performance.openDefects}</Typography>
        ) : '—'
      ),
    },
    {
      id: 'payment',
      label: 'Total Paid',
      minWidth: 120,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => formatMoney(row.payment.totalPaid),
    },
    {
      id: 'payment',
      label: 'Awaiting Approval',
      minWidth: 140,
      align: 'right',
      format: (_value: any, row: TeamOverviewRow) => (
        row.payment.awaitingApproval > 0 ? (
          <Typography variant="body2" fontWeight="bold" color="warning.main">{formatMoney(row.payment.awaitingApproval)}</Typography>
        ) : '—'
      ),
    },
  ];

  return (
    <Box>
      {!embedded && (
        <PageHeader
          title="Teams Overview"
          subtitle={scopeToProject && activeProjectId ? 'Scoped to active project' : 'All projects'}
        />
      )}

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Active Teams" value={`${filtered.length}`} icon={<Group />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Total Members" value={`${summary.totalMembers}`} icon={<Group />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Tasks In Progress" value={`${summary.totalInProgress}`} icon={<Engineering />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Avg First-Pass Yield"
            value={summary.avgYield !== null ? `${summary.avgYield}%` : '—'}
            icon={<Speed />}
            color={summary.avgYield === null ? 'primary' : summary.avgYield >= 90 ? 'success' : summary.avgYield >= 75 ? 'warning' : 'error'}
          />
        </Grid>
      </Grid>

      {!embedded && (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Payments Awaiting Approval"
            value={formatMoney(summary.totalAwaitingApproval)}
            changeLabel={summary.totalAwaitingApproval > 0 ? 'Across all visible teams' : 'All caught up'}
            icon={<Payments />}
            color={summary.totalAwaitingApproval > 0 ? 'warning' : 'success'}
          />
        </Grid>
      </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Team type</InputLabel>
          <Select value={teamTypeFilter} label="Team type" onChange={(e) => setTeamTypeFilter(e.target.value)}>
            <MenuItem value="all">All types</MenuItem>
            {Object.entries(TEAM_TYPE_LABEL).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            value={scopeToProject ? 'project' : 'all'}
            label="Scope"
            onChange={(e) => setScopeToProject(e.target.value === 'project')}
          >
            <MenuItem value="project" disabled={!activeProjectId}>Active project only</MenuItem>
            <MenuItem value="all">All projects</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No teams match the current filter.</Alert>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable columns={columns} data={filtered} pagination searchable onRowClick={(row) => setSelected(row)} />
        </Box>
      )}

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <Box sx={{ width: 380, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">{selected.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {TEAM_TYPE_LABEL[selected.teamType] ?? selected.teamType} · {selected.memberCount} member{selected.memberCount === 1 ? '' : 's'}
            </Typography>
            {selected.teamLeadName && (
              <Typography variant="body2">Lead: <strong>{selected.teamLeadName}</strong></Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Workload</Typography>
            <Typography variant="body2">In progress: {selected.workload.inProgress} / {selected.maxConcurrentTasks}</Typography>
            <Typography variant="body2">Blocked: {selected.workload.blocked}</Typography>
            <Typography variant="body2">Scheduled: {selected.workload.scheduled}</Typography>
            <Typography variant="body2">Inspection pending: {selected.workload.inspectionPending}</Typography>
            <Typography variant="body2">Completed: {selected.workload.completed}</Typography>
            <Typography variant="body2">Cancelled: {selected.workload.cancelled}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Performance</Typography>
            <Typography variant="body2">Tasks touched: {selected.performance.tasksTouched}</Typography>
            <Typography variant="body2">First-pass yield: {selected.performance.firstPassYieldPercent !== null ? `${selected.performance.firstPassYieldPercent}%` : '—'}</Typography>
            <Typography variant="body2">Open defects: {selected.performance.openDefects}</Typography>
            <Typography variant="body2">Closed defects: {selected.performance.closedDefects}</Typography>
            <Typography variant="body2">Rework count: {selected.performance.reworkCount}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Payment</Typography>
            <Typography variant="body2">Total approved: {formatMoney(selected.payment.totalApproved)}</Typography>
            <Typography variant="body2">Total paid: {formatMoney(selected.payment.totalPaid)} ({selected.payment.paidCount} payments)</Typography>
            <Typography variant="body2">Awaiting approval: {formatMoney(selected.payment.awaitingApproval)}</Typography>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
